import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import {
  DOWNLOADABLE_FILE_CATEGORIES,
  FILE_TYPE_LABEL,
  GEO_EXT,
  GEO_EXT_TOOLTIP,
  SESSION_STATUS,
  SESSION_UPLOAD_STATUS_CLIENT,
} from "../../constants";
import { createContext, useContext } from "react";

import DatasetUploader from "./DatasetFileUploader";
import DatasetsTable from "./DatasetsTable";
import { InlineSpinner } from "../../components/InlineSpinner";
import { addWarningAlert } from "../../actions/alerts";
import { isUploadSessionEditable } from "../../services/uploadSessionUtils";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

const STATUS_REQUIRED_HINT =
  'Please choose a status under "Mark this session as" before saving.';

export const FormContext = createContext({
  loading: false,
  datasetCount: 0,
  onChange: () => {},
  values: {},
  session: {},
  statistics: [],
  statusFinish: SESSION_STATUS.Completed,
  comment: "",
  setComment: () => {},
  status: "",
  setStatus: () => {},
  wasLoading: false,
  uploadFailed: false,
  user: {},
  editable: true,
});

const onChangeScalar = (type, key, values, onChange) => {
  return (event) => {
    onChange({
      ...values,
      event,
      type,
      key,
      data: { [key]: event.target.value },
    });
  };
};

function FormTemplate({ children }) {
  return <div className="m-1">{children}</div>;
}

const ScalarInput = ({ label, compact, ...forInput }) => {
  const context = useContext(FormContext);
  const { id, type, required, helptooltip } = forInput;
  const value = context.values[id] ?? "";
  const hasValue = !!value;
  const isRequired = !hasValue && required;

  const fileInfo =
    type === "file" && DOWNLOADABLE_FILE_CATEGORIES.includes(id)
      ? context.statistics?.find((stat) => stat.category === id)
      : null;

  const inputProps = {
    ...forInput,
    ...(type !== "file" && { value }),
    required: isRequired,
    onChange: onChangeScalar(type, id, context.values, context.onChange),
  };

  return (
    <LabelInput
      label={`${label} ${inputProps.required ? " *" : ""}`}
      helptooltip={helptooltip}
      fileInfo={fileInfo}
      compact={compact}
    >
      {type === "file" ? (
        <div className="upload-file-field">
          <div className="input-group">
            <Input
              {...inputProps}
              className="form-control"
              invalid={!!context.fileErrors?.[id]}
              disabled={context.loading || !context.editable}
            />
          </div>
          {value && (
            <div
              className="upload-file-current text-muted small text-break"
              title={value}
            >
              <i className="fa-solid fa-paperclip me-1" />
              {value}
            </div>
          )}
        </div>
      ) : (
        <Input
          {...inputProps}
          disabled={context.loading || !context.editable}
        />
      )}
    </LabelInput>
  );
};

const LabelInput = ({
  label,
  icon,
  helptooltip,
  children,
  fileInfo,
  compact,
}) => {
  // Plain div (not FormGroup) so Reactstrap doesn't inject its `mb-3` margin on
  // the field wrappers (sensor, altitude, file pickers).
  return (
    <div className={compact ? "upload-file-compact" : undefined}>
      <Label>
        <i className={icon} aria-hidden="true"></i> {label}
        {helptooltip && (
          <i
            className="fa-solid fa-lg fa-circle-info collapseToggleTitle"
            data-tooltip-id="tooltip"
            data-tooltip-content={helptooltip}
          />
        )}
        {fileInfo?.download_url && (
          <span className="text-muted small ms-2">
            (
            <a
              href={fileInfo.download_url}
              download={fileInfo.filename}
              style={{ color: "#8fbf48" }}
            >
              {fileInfo.filename}
              <i className="fa-solid fa-download ms-1" />
            </a>
            )
          </span>
        )}
      </Label>

      {children}
    </div>
  );
};

/** Collapsible "Previous Datasets" panel (with the download button), shown at
 * the bottom of the files column when the session already has statistics. */
const PreviousDatasets = () => {
  const context = useContext(FormContext);
  if (!(context.statistics?.length > 0)) return null;
  return (
    <DatasetsTable
      statistics={context.statistics}
      uploadSession={context.session}
      user={context.user}
    />
  );
};

const CommentField = () => {
  const context = useContext(FormContext);
  // Plain div (not FormGroup) so Reactstrap doesn't inject its `mb-3` margin,
  // which would break the comment's bottom alignment with the status card.
  return (
    <div className="upload-comment-group">
      <Label>Comments</Label>
      <textarea
        value={context.comment ?? ""}
        className="form-control upload-comment"
        placeholder="Comments"
        onChange={(e) => context.setComment(e.target.value)}
        disabled={context.loading || !context.editable}
      />
    </div>
  );
};

const SubmitAction = () => {
  const context = useContext(FormContext);
  const dispatch = useDispatch();

  // A locked (completed / synchronised) session can no longer be saved.
  if (!context.editable) return null;

  const { loading, wasLoading, status, uploadFailed } = context;
  const missingStatus = !status;
  const failed = wasLoading && uploadFailed;

  // Disabled buttons swallow clicks, so we warn from the wrapping span (which
  // receives the click thanks to `pointer-events: none` on the disabled button).
  const handleHintClick = () => {
    if (missingStatus && !loading)
      dispatch(addWarningAlert(STATUS_REQUIRED_HINT));
  };

  return (
    <div className="upload-submit-action">
      {loading && (
        <span className="me-3 text-muted small">
          <InlineSpinner className="me-2" />
          This may take a while.
        </span>
      )}

      {wasLoading && !loading && !uploadFailed && (
        <span className="text-success me-3 small">Successfully uploaded.</span>
      )}

      {failed && !loading && (
        <span className="text-danger me-3 small">
          <i
            className="fa-solid fa-circle-exclamation me-1"
            aria-hidden="true"
          />
          A problem occurred during synchronization, please retry.
        </span>
      )}

      <span
        onClick={handleHintClick}
        data-tooltip-id="tooltip"
        data-tooltip-content={missingStatus ? STATUS_REQUIRED_HINT : undefined}
      >
        <Button
          color="primary"
          className={
            missingStatus ? "is-disabled upload-submit-btn-disabled" : undefined
          }
          disabled={loading || missingStatus}
          type="submit"
        >
          <span>Upload & Save</span>
          {loading && <InlineSpinner className="ms-2" />}
        </Button>
      </span>
    </div>
  );
};

const StatusField = () => {
  const context = useContext(FormContext);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Choosing a status only stages it locally, remind the user the session is
  // not persisted until they click "Upload & Save".
  const chooseStatus = (value) => {
    if (value !== context.status)
      enqueueSnackbar(
        'Don\'t forget to click "Upload & Save" to save your session.',
        {
          variant: "warning",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          action: (snackbarId) => (
            <span
              className="clickable font-weight-bold"
              onClick={() => closeSnackbar(snackbarId)}
            >
              Dismiss
            </span>
          ),
        }
      );
    context.setStatus(value);
  };

  // Completed / synchronised sessions are read-only: show a notice instead of
  // the clickable status options.
  if (!context.editable)
    return (
      <FormGroup className="upload-status-group">
        <Label className="upload-status-label">Status</Label>
        <p className="upload-status-locked">
          <i className="fa-solid fa-lock me-2" aria-hidden="true" />
          This session has been marked as completed and is no longer editable.
        </p>
      </FormGroup>
    );

  return (
    <FormGroup className="upload-status-group">
      <Label className="upload-status-label">
        Mark this session as <span className="text-danger">*</span>
      </Label>
      <div className="session-upload-status-choice">
        {SESSION_UPLOAD_STATUS_CLIENT.map((option) => {
          const selected = option.value === context.status;
          return (
            <div
              key={option.value}
              role="button"
              tabIndex={0}
              className={`status-option ${option.value}${
                selected ? " selected" : ""
              }`}
              onClick={() => chooseStatus(option.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  chooseStatus(option.value);
                }
              }}
            >
              <span className="status-title">{option.title}</span>
              <span className="status-description">{option.label}</span>
            </div>
          );
        })}
      </div>
      {context.status === "complete" && (
        <div className="upload-status-warning">
          <i
            className="fa-solid fa-triangle-exclamation me-1"
            aria-hidden="true"
          />
          A session saved as COMPLETE is no longer editable.
        </div>
      )}
    </FormGroup>
  );
};

/** Status selector and the Upload button grouped into a single card so the
 * action reads as the natural conclusion of choosing a status. */
const StatusCard = () => {
  return (
    <Card className="upload-status-card">
      <CardBody>
        <StatusField />
        <SubmitAction />
      </CardBody>
    </Card>
  );
};

/** Compact reference-file pickers (plot, gcp, metadata). Live on the right-hand
 * params side so the dropzone owns the left column on its own. */
const ReferenceFileInputs = () => {
  return (
    <div className="upload-file-inputs">
      <ScalarInput
        compact
        label={FILE_TYPE_LABEL.plot_file}
        accept={GEO_EXT.plot_file.join(",")}
        type="file"
        id="plot_file"
        helptooltip={GEO_EXT_TOOLTIP}
      />
      <ScalarInput
        compact
        label={FILE_TYPE_LABEL.gcp_file}
        accept={GEO_EXT.gcp_file.join(",")}
        type="file"
        id="gcp_file"
        helptooltip={GEO_EXT_TOOLTIP}
      />
      <ScalarInput
        compact
        label={FILE_TYPE_LABEL.metadata_file}
        type="file"
        id="metadata_file"
      />
    </div>
  );
};

/** Right-hand panel: acquisition params (sensor + altitude on one row, stacking
 * on narrow screens), then the reference-file pickers, then the status card. */
const UavParamsColumn = () => {
  return (
    <Col md={5} className="upload-metadata-col">
      <div className="upload-metadata-panel">
        <Row className="g-2">
          <Col xs={12} sm={6}>
            <ScalarInput
              label={FILE_TYPE_LABEL.sensor}
              type="text"
              id="sensor"
            />
          </Col>
          <Col xs={12} sm={6}>
            <ScalarInput
              label={FILE_TYPE_LABEL.flight_altitude}
              type="number"
              id="flight_altitude"
              min="0"
            />
          </Col>
        </Row>
        <ReferenceFileInputs />
        <StatusCard />
      </div>
    </Col>
  );
};

/** Shared UAV layout: dropzone + comment on the left, params (sensor/altitude),
 * reference files and status card on the right. `extraLeft` renders above the
 * uploader (e.g. the archive name). */
const UavForm = ({ uploader, extraLeft }) => {
  return (
    <FormTemplate>
      <Row className="g-3">
        <Col md={7} className="upload-files-col">
          {extraLeft}
          {uploader}
          <CommentField />
        </Col>
        <UavParamsColumn />
      </Row>
      <PreviousDatasets />
    </FormTemplate>
  );
};

/** Templates with no scalar params: uploader + comment left, status card right. */
const SimpleForm = ({ uploader }) => {
  return (
    <FormTemplate>
      <Row className="g-3">
        <Col md={7} className="upload-files-col">
          {uploader}
          <CommentField />
        </Col>
        <Col md={5} className="upload-metadata-col">
          <div className="upload-metadata-panel">
            <StatusCard />
          </div>
        </Col>
      </Row>
      <PreviousDatasets />
    </FormTemplate>
  );
};

function UploadForm() {
  return <SimpleForm uploader={<DatasetUploader />} />;
}

function RoverForm() {
  return <SimpleForm uploader={<DatasetUploader />} />;
}

function LiteralForm() {
  return <SimpleForm uploader={<DatasetUploader />} />;
}

function UavRgbForm() {
  return (
    <UavForm
      uploader={
        <DatasetUploader
          options={{ restrictions: { allowedFileTypes: ["image/*"] } }}
        />
      }
    />
  );
}

function UavMultispectralForm() {
  return <UavForm uploader={<DatasetUploader />} />;
}

function UavThermalForm() {
  return (
    <UavForm
      uploader={
        <DatasetUploader
          options={{
            restrictions: { allowedFileTypes: ["image/*", ".tmc"] },
          }}
        />
      }
    />
  );
}

function UavArchiveForm() {
  return (
    <UavForm
      extraLeft={
        <ScalarInput
          label={FILE_TYPE_LABEL.archive_filename}
          type="text"
          id="archive_filename"
          placeholder="file.zip"
        />
      }
      uploader={
        <DatasetUploader
          options={{
            restrictions: { allowedFileTypes: ["image/*", ".tmc"] },
          }}
        />
      }
    />
  );
}

function UavLidarForm() {
  return (
    <UavForm
      uploader={
        <DatasetUploader
          options={{
            restrictions: {
              allowedFileTypes: [
                "image/*",
                ".rpt",
                ".clc",
                ".mrk",
                ".cli",
                ".dbg",
                ".imu",
                ".ldr",
                ".ldrt",
                ".rpos",
                ".rtk",
                ".rtl",
                ".rts",
                ".sig",
                ".cmi",
                ".rtb",
              ],
            },
          }}
        />
      }
    />
  );
}

const UploadTemplate = {
  UavMultispectral: "uav_multispectral",
  UavThermal: "uav_thermal",
  UavRgb: "uav",
  Rover: "rover",
  Literal: "literal",
  Upload: "upload",
  UavArchive: "uav_archive",
  UavLidar: "uav_lidar",
};

const LoadingTemplate = () => <div>Loading Template...</div>;

/**
 * Returns the template form react component for a given template type.
 *
 * @param template template form you would like
 * @returns the template form react component
 */
function getFormTemplate(template) {
  switch (template) {
    case UploadTemplate.UavThermal:
      return UavThermalForm;
    case UploadTemplate.UavMultispectral:
      return UavMultispectralForm;
    case UploadTemplate.UavRgb:
      return UavRgbForm;
    case UploadTemplate.Rover:
      return RoverForm;
    case UploadTemplate.Literal:
      return LiteralForm;
    case UploadTemplate.Upload:
      return UploadForm;
    case UploadTemplate.UavArchive:
      return UavArchiveForm;
    case UploadTemplate.UavLidar:
      return UavLidarForm;
    default:
      return LoadingTemplate;
  }
}

export function renderFormTemplate(
  template,
  input,
  datasetCount,
  loading,
  statusFinish
) {
  const FormTemplate = getFormTemplate(template);
  return (
    <FormContext.Provider
      value={{
        loading,
        datasetCount,
        session: input.session,
        values: input.data,
        fileErrors: input.fileErrors || {},
        onChange: input.onChange,
        statusFinish,
        statistics: input.session?.statistics || [],
        comment: input.comment,
        setComment: input.setComment,
        status: input.status,
        setStatus: input.setStatus,
        wasLoading: input.wasLoading,
        uploadFailed: input.uploadFailed,
        user: input.user,
        editable: input.session ? isUploadSessionEditable(input.session) : true,
      }}
    >
      <FormTemplate {...input} />
    </FormContext.Provider>
  );
}
